import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useClientAuth } from '@/hooks/useClientAuth';
import { useAuth } from '@/hooks/useAuth';
import { CosmosWaveLayout } from '@/components/dashboard/CosmosWaveLayout';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { KpiCards } from '@/components/dashboard/KpiCards';
import { DashboardTabs } from '@/components/dashboard/DashboardTabs';
import { AlertPanel } from '@/components/dashboard/AlertPanel';
import { GlobalFilters, type FilterState } from '@/components/dashboard/GlobalFilters';
import { Loader2, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';

export default function ClientDashboard() {
  const { clientSlug } = useParams<{ clientSlug: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { currentClient, switchClient, loading: clientLoading } = useClientAuth();
  
  const [filters, setFilters] = useState<FilterState>({
    dateRange: { from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), to: new Date() },
    marketplace: 'all',
    brand: 'all',
    category: 'all',
    sku: '',
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/espace-client');
      return;
    }

    if (clientSlug && currentClient?.slug !== clientSlug) {
      switchClient(clientSlug);
    }
  }, [user, clientSlug, currentClient, authLoading, switchClient, navigate]);

  if (authLoading || clientLoading) {
    return (
      <CosmosWaveLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF8A00]" />
        </div>
      </CosmosWaveLayout>
    );
  }

  if (!user || !currentClient) {
    return null; // Redirecting
  }

  if (currentClient.slug !== clientSlug) {
    return (
      <CosmosWaveLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-2 text-[#E6EAED]">Client non trouvé</h1>
            <p className="text-[#E6EAED]/70 mb-4">
              Vous n'avez pas accès à ce client ou il n'existe pas.
            </p>
            <button
              onClick={() => navigate('/espace-client')}
              className="text-[#FF8A00] hover:underline"
            >
              Retour à la sélection de client
            </button>
          </div>
        </div>
      </CosmosWaveLayout>
    );
  }

  return (
    <>
      <SEOHead 
        title={`Dashboard ${currentClient.name} - Analytics Amazon`}
        description={`Dashboard complet pour ${currentClient.name}. Suivi des performances Amazon, KPIs temps réel, insights IA et optimisations.`}
      />
      
      <CosmosWaveLayout>
        <DashboardHeader client={currentClient} />
        
        <div className="container mx-auto p-6 space-y-6">
          <div className="flex items-center justify-between">
            <GlobalFilters filters={filters} onFiltersChange={setFilters} />
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/dashboard/${clientSlug}/settings`)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Paramètres API
            </Button>
          </div>
          
          <KpiCards clientId={currentClient.id} filters={filters} />
          
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3">
              <DashboardTabs clientId={currentClient.id} filters={filters} />
            </div>
            <div className="lg:col-span-1">
              <AlertPanel clientId={currentClient.id} />
            </div>
          </div>
        </div>
      </CosmosWaveLayout>
    </>
  );
}